'use client';

/**
 * CommandPaletteModeToggle Component
 *
 * Toggle between search and chat modes in the Command Palette.
 *
 * @module components/CommandPalette/CommandPaletteModeToggle
 * @see Issue #236 - Cross-Entity Global Search
 */

import ChatIcon from '@mui/icons-material/Chat';
import SearchIcon from '@mui/icons-material/Search';
import { alpha, Box, ButtonBase, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { useI18n } from '../../hooks/useI18n';
import type { CommandPaletteMode, CommandPaletteModeToggleProps } from './types';

// ============================================================================
// Styled Components
// ============================================================================

const ToggleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5),
  borderRadius: 8,
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.05) : alpha(theme.palette.common.black, 0.04),
}));

interface ToggleButtonProps {
  $isActive: boolean;
}

const ToggleButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<ToggleButtonProps>(({ theme, $isActive }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  padding: theme.spacing(0.5, 1.25),
  borderRadius: 6,
  fontSize: '0.8125rem',
  fontWeight: $isActive ? 600 : 400,
  color: $isActive ? theme.palette.primary.main : theme.palette.text.secondary,
  backgroundColor: $isActive
    ? theme.palette.mode === 'dark'
      ? alpha(theme.palette.primary.main, 0.15)
      : alpha(theme.palette.primary.main, 0.1)
    : 'transparent',
  transition: 'all 150ms ease',

  '&:hover': {
    backgroundColor: $isActive
      ? theme.palette.mode === 'dark'
        ? alpha(theme.palette.primary.main, 0.2)
        : alpha(theme.palette.primary.main, 0.15)
      : theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.08)
        : alpha(theme.palette.common.black, 0.06),
  },

  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

const IconWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& > svg': {
    fontSize: '1rem',
  },
});

const HintContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginLeft: theme.spacing(1),
  color: theme.palette.text.secondary,
  fontSize: '0.75rem',
}));

const KeyboardKey = styled('kbd')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 20,
  height: 20,
  padding: '0 4px',
  fontSize: '0.6875rem',
  fontFamily: 'inherit',
  fontWeight: 600,
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.08) : alpha(theme.palette.common.black, 0.06),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 3,
  boxShadow: `0 1px 0 ${theme.palette.divider}`,
}));

// ============================================================================
// Component
// ============================================================================

export const CommandPaletteModeToggle: React.FC<CommandPaletteModeToggleProps> = ({
  mode,
  onModeChange,
  chatEnabled = true,
  showHint = true,
}) => {
  const { t } = useI18n();

  const handleModeChange = (newMode: CommandPaletteMode) => {
    if (newMode !== mode) {
      onModeChange(newMode);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <ToggleContainer>
        <ToggleButton
          $isActive={mode === 'search'}
          onClick={() => handleModeChange('search')}
          aria-pressed={mode === 'search'}
          aria-label={t('palette.mode.search')}
        >
          <IconWrapper>
            <SearchIcon />
          </IconWrapper>
          <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
            {t('palette.mode.search')}
          </Typography>
        </ToggleButton>

        {chatEnabled && (
          <ToggleButton
            $isActive={mode === 'chat'}
            onClick={() => handleModeChange('chat')}
            aria-pressed={mode === 'chat'}
            aria-label={t('palette.mode.chat')}
          >
            <IconWrapper>
              <ChatIcon />
            </IconWrapper>
            <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
              {t('palette.mode.chat')}
            </Typography>
          </ToggleButton>
        )}
      </ToggleContainer>

      {showHint && chatEnabled && (
        <HintContainer>
          <KeyboardKey>Tab</KeyboardKey>
          <Typography variant="caption" sx={{ fontSize: 'inherit', color: 'inherit' }}>
            {mode === 'search' ? t('palette.mode.switchToChat') : t('palette.mode.switchToSearch')}
          </Typography>
        </HintContainer>
      )}
    </Box>
  );
};

CommandPaletteModeToggle.displayName = 'CommandPaletteModeToggle';

export default CommandPaletteModeToggle;
